import { useRef, useState } from 'react'
import {
    LuArrowDown,
    LuArrowUpRight,
    LuBookOpen,
    LuCodeXml,
    LuPenTool,
} from 'react-icons/lu'
import ScrollIndicator from '../ScrollIndicator'
import MakerDoodle from '../MakerDoodle'
import FloatingIcons from '../FloatingIcons'
import './Hero.css'

const ideaCards = [
    {
        id: 'build',
        label: 'Build',
        number: '01',
        icon: LuCodeXml,
        category: 'Software engineering',
        title: 'Making ideas actually work.',
        note: "I like figuring out how things work, why they don't, and how to make them better. From the behind-the-scenes logic to the tiny details on screen, I enjoy piecing everything together until it clicks.",
        ingredients: ['Problem-solving', 'Coding', 'Making it work'],
    },
    {
        id: 'imagine',
        label: 'Imagine',
        number: '02',
        icon: LuPenTool,
        category: 'Product design',
        title: 'Useful can be fun, too.',
        note: "I'm drawn to the space where design and technology meet. I love playing with ideas, trying things out, and finding those little touches that make something easier (and a lot more enjoyable) to use.",
        ingredients: ['Curiosity', 'Playful details', 'Purpose'],
    },
    {
        id: 'teach',
        label: 'Teach',
        number: '03',
        icon: LuBookOpen,
        category: 'Learning & education',
        title: 'Curiosity is contagious.',
        note: "I've always loved the idea that learning doesn't have to feel like learning. I want to make room for exploration, creativity, and those little aha! moments when something finally makes sense.",
        ingredients: ['Experiential', 'Connectivity', 'Inspiration'],
    },
]

const IdeaCollection = () => {
    const [activeIndex, setActiveIndex] = useState(0)
    const tabRefs = useRef([])
    const activeCard = ideaCards[activeIndex]

    const handleTabKeyDown = (event, index) => {
        let nextIndex

        if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % ideaCards.length
        } else if (event.key === 'ArrowLeft') {
            nextIndex = (index + ideaCards.length - 1) % ideaCards.length
        } else if (event.key === 'Home') {
            nextIndex = 0
        } else if (event.key === 'End') {
            nextIndex = ideaCards.length - 1
        } else {
            return
        }

        event.preventDefault()
        setActiveIndex(nextIndex)
        tabRefs.current[nextIndex]?.focus()
    }

    return (
        <div className="personal-desk">
            <svg
                className="personal-desk__flower"
                viewBox="0 0 106 118"
                fill="none"
                aria-hidden="true"
            >
                <path
                    d="M47 50C30 35 29 11 44 10C59 9 64 30 54 47C61 27 84 24 91 37C97 51 77 61 57 54C79 55 89 75 77 85C65 95 52 75 51 59C48 79 29 93 18 81C7 68 27 54 44 54C24 59 6 45 15 33C24 21 43 37 47 50Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M54 49C59 55 54 62 48 60C42 58 43 48 50 47C54 46 59 49 58 54M50 62C47 79 56 92 46 105C36 117 20 106 25 99C30 92 39 102 35 111"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            </svg>

            <div className="personal-desk__box">
                <div
                    className="personal-desk__tabs"
                    role="tablist"
                    aria-label="Explore my interests"
                >
                    {ideaCards.map((card, index) => {
                        const Icon = card.icon

                        return (
                            <button
                                className={`personal-desk__tab personal-desk__tab--${card.id}`}
                                id={`idea-tab-${card.id}`}
                                key={card.id}
                                type="button"
                                role="tab"
                                aria-selected={activeIndex === index}
                                aria-controls={`idea-panel-${card.id}`}
                                tabIndex={activeIndex === index ? 0 : -1}
                                ref={(element) => {
                                    tabRefs.current[index] = element
                                }}
                                onClick={() => setActiveIndex(index)}
                                onKeyDown={(event) =>
                                    handleTabKeyDown(event, index)
                                }
                            >
                                <Icon aria-hidden="true" />
                                {card.label}
                            </button>
                        )
                    })}
                </div>
                <div className="personal-desk__paper-stack">
                    {ideaCards.map((card) => (
                        <div
                            className={`personal-desk__card personal-desk__card--${card.id}`}
                            id={`idea-panel-${card.id}`}
                            key={card.id}
                            role="tabpanel"
                            aria-labelledby={`idea-tab-${card.id}`}
                            hidden={activeCard.id !== card.id}
                            tabIndex={0}
                        >
                            <div className="personal-desk__card-topline">
                                <span className="personal-desk__card-category">
                                    {card.category}
                                </span>
                                <span className="personal-desk__card-number">
                                    No. {card.number}
                                </span>
                            </div>
                            <h2>{card.title}</h2>
                            <p className="personal-desk__card-note">
                                {card.note}
                            </p>
                            <div className="personal-desk__ingredients">
                                <ul aria-label="Ingredients for my work">
                                    {card.ingredients.map((ingredient) => (
                                        <li key={ingredient}>{ingredient}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="personal-desk__pencil" aria-hidden="true">
                <span />
            </div>
        </div>
    )
}

const Hero = () => {
    return (
        <section id="home" className="hero" aria-labelledby="hero-title">
            <FloatingIcons variant="hero" />
            <div className="site-container">
                <div className="hero__grid">
                    <div className="hero__content">
                        <p className="eyebrow">
                            Software engineering · Design · Education
                        </p>
                        <p className="hero__hello">Hello, I'm</p>
                        <h1 id="hero-title">
                            Josephine
                            <br />
                            <span className="hero__name-accent">
                                Nguyen!
                                <svg
                                    className="hero__name-underline"
                                    viewBox="0 0 330 18"
                                    fill="none"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path d="M5 9C81 1 229 2 322 8M15 15C126 8 231 10 307 14" />
                                </svg>
                            </span>
                        </h1>
                        <p className="hero__description">
                            I build thoughtful digital experiences that make
                            learning a little more human.
                        </p>
                        <p className="hero__detail">
                            Full-stack developer, creative problem solver, and a
                            believer in technology with a purpose.
                        </p>
                        <div className="hero__actions">
                            <a
                                className="button button--primary"
                                href="#projects"
                            >
                                Explore my work{' '}
                                <LuArrowDown aria-hidden="true" />
                            </a>
                            <a
                                className="button button--secondary"
                                href="mailto:the.josephine.nguyen@gmail.com"
                            >
                                Let's connect{' '}
                                <LuArrowUpRight aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                    <IdeaCollection />
                </div>
                <div className="hero__footer">
                    <MakerDoodle variant="thread" className="hero__seam" />
                    <ScrollIndicator />
                </div>
            </div>
        </section>
    )
}

export default Hero
